import {useMemo} from 'react'
import {Interface} from '@ethersproject/abi'
import {Currency, CurrencyAmount, Ether, Token} from '@uniswap/sdk-core'
import {ChainId} from '@sushiswap/sdk'
import JSBI from 'jsbi'
import {isAddress} from '../../utils/web3'
import {useActiveWeb3React} from '../../hooks/web3'
import {useMulticall2Contract} from '../../hooks/useContract'
import {useMultipleContractSingleData, useSingleContractMultipleData} from '../multicall/hooks'
import {OVL, LL} from '../../constants/tokens'
import ERC20_ABI from '../../constants/abis/erc20.json'

/**
 * Returns a map of the given addresses to their eventually consistent ETH balances.
 */
export function useETHBalances(uncheckedAddresses?: (string | undefined)[]): {
  [address: string]: CurrencyAmount<Currency> | undefined
} {
  const {chainId} = useActiveWeb3React()
  const multicallContract = useMulticall2Contract()

  const addresses: string[] = useMemo(
    () =>
      uncheckedAddresses
        ? uncheckedAddresses
            .map(isAddress)
            .filter((a): a is string => a !== false)
            .sort()
        : [],
    [uncheckedAddresses],
  )

  const results = useSingleContractMultipleData(
    multicallContract,
    'getEthBalance',
    addresses.map(address => [address]),
  )

  return useMemo(
    () =>
      addresses.reduce<{[address: string]: CurrencyAmount<Currency>}>((memo, address, i) => {
        const value = results?.[i]?.result?.[0]
        if (value && chainId) memo[address] = CurrencyAmount.fromRawAmount(Ether.onChain(chainId), JSBI.BigInt(value.toString()))
        return memo
      }, {}),
    [addresses, chainId, results],
  )
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[],
): [{[tokenAddress: string]: CurrencyAmount<Token> | undefined}, boolean] {
  const validatedTokens: Token[] = useMemo(() => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false) ?? [], [tokens])

  const validatedTokenAddresses = useMemo(() => validatedTokens.map(vt => vt.address), [validatedTokens])
  const ERC20Interface = new Interface(ERC20_ABI)
  const balances = useMultipleContractSingleData(validatedTokenAddresses, ERC20Interface, 'balanceOf', [address], undefined, 100_000)

  const anyLoading: boolean = useMemo(() => balances.some(callState => callState.loading), [balances])

  return [
    useMemo(
      () =>
        address && validatedTokens.length > 0
          ? validatedTokens.reduce<{
              [tokenAddress: string]: CurrencyAmount<Token> | undefined
            }>((memo, token, i) => {
              const value = balances?.[i]?.result?.[0]
              const amount = value ? JSBI.BigInt(value.toString()) : undefined
              if (amount) {
                memo[token.address] = CurrencyAmount.fromRawAmount(token, amount)
              }
              return memo
            }, {})
          : {},
      [address, validatedTokens, balances],
    ),
    anyLoading,
  ]
}

export function useTokenBalances(address?: string, tokens?: (Token | undefined)[]): {[tokenAddress: string]: CurrencyAmount<Token> | undefined} {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): CurrencyAmount<Token> | undefined {
  const tokenBalances = useTokenBalances(account, [token])
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useOvlBalance(): CurrencyAmount<Token> | undefined {
  const {account, chainId} = useActiveWeb3React()

  const ovl = chainId ? OVL[chainId] : undefined

  const ovlBalance: CurrencyAmount<Token> | undefined = useTokenBalance(account ?? undefined, ovl)

  if (!ovl) return undefined

  return CurrencyAmount.fromRawAmount(ovl, ovlBalance?.quotient ?? JSBI.BigInt(0))
}

export function useChainOvlBalance(chainId: ChainId | undefined): CurrencyAmount<Token> | undefined {
  const {account} = useActiveWeb3React()

  const ovl = chainId ? OVL[chainId] : undefined

  const ovlBalance: CurrencyAmount<Token> | undefined = useTokenBalance(account ?? undefined, ovl)

  if (!ovl) return undefined

  return CurrencyAmount.fromRawAmount(ovl, ovlBalance?.quotient ?? JSBI.BigInt(0))
}

export function useChainLLBalance(chainId: ChainId | undefined): CurrencyAmount<Token> | undefined {
  const {account} = useActiveWeb3React()

  const ll = chainId ? LL[chainId] : undefined

  const llBalance: CurrencyAmount<Token> | undefined = useTokenBalance(account ?? undefined, ll)

  if (!ll) return undefined

  return CurrencyAmount.fromRawAmount(ll, llBalance?.quotient ?? JSBI.BigInt(0))
}
